/**
 * This file contains the complete filter objects for the frontend Algolia filters (including their widgets).
 * If you update it, you need to also update frontend-filter-options.ts,
 * which contains the basic filter definitions.
 * These need to be in separate files because the ContentfulController seems to have issues
 * importing the instantsearch.js widgets.
 */

import type { TFunction } from 'i18next';
export type WidgetType = 'toggle' | 'refinementList' | 'range' | 'numericMenu';

import {
  panel,
  rangeSlider,
  refinementList,
  toggleRefinement,
} from 'instantsearch.js/es/widgets';
import { parseFirstNumber } from '../utils';

export class Filter {
  /** Heading for the filter */
  public title: string;
  /** The algolia attribute to filter on */
  public attribute: string;
  /** The ID to use in the DOM */
  public id: string;
  public limit?: number;
  public showMore?: boolean;
  /**
   * These are the route-state values, not the actual widget class
   * e.g. 'toggle', not `toggleRefinement`
   */
  public widgetType: WidgetType;
  /** This is a function that *returns* the widget,
   * since instantiating the widget requires the DOM element to be on the page,
   * and some are omitted on specific collections */
  public generateWidget: () => any; // TODO: Add types

  constructor(params: {
    title: string;
    attribute: string;
    id: string;
    limit?: number;
    showMore?: boolean;
    widgetType: WidgetType;
    generateWidget: () => any;
  }) {
    this.title = params.title;
    this.attribute = params.attribute;
    this.id = params.id;
    this.limit = params.limit;
    this.showMore = params.showMore;
    this.widgetType = params.widgetType;
    this.generateWidget = params.generateWidget;
  }
}

/**
 * Wrap an algolia widget with a panel widget
 * @param label The filter label
 * @param widget The widget to wrap with a panel
 * @param widgetOptions The options to pass to the widget
 */
const wrapWithPanel = (label: string, widget, widgetOptions) => {
  const p = panel({
    hidden(options) {
      const { items } = options;
      // Range slider doesn't have `options`, so always show it
      const hasItems = items && Array.isArray(items);
      if (!hasItems) {
        return false;
      }
      // Hide if only single option (or none)
      return items.length <= 1;
    },
    templates: {
      // @ts-ignore
      header(options, { html }) {
        return html`
        <button class="filters__accordion-toggle js-accordion-toggle">
          <h4 class="filters__filter-title">${label}</h4>
        </button>`;
      },
    },
  })(widget);

  return p(widgetOptions);
};

/**
 * Definition of all the different filters we could use
 * NOTE: You need to add to the filterOptions object in frontend-filter-options.ts
 * when adding a new filter here
 */
const filterObjects = (locale: string, t: TFunction) => {
  const tagPrefix = locale.toUpperCase() + '_';

  const filterObjects: Record<string, Filter> = {
    availableOnline: new Filter({
      title: t('algolia:filters.availableOnline.label'),
      attribute: 'named_tags.available',
      id: 'availableOnline',
      widgetType: 'toggle',
      generateWidget: function() {
        const filter = this;
        return toggleRefinement({
          container: `#${this.id}`,
          attribute: this.attribute,
          on: 'online',
          templates: {
            labelText(_item, { html }) {
              return html`${filter.title}`;
            },
          },
        });
      },
    }),
    // This is labelled as "Product Type" in the UI
    category: new Filter({
      title: t('algolia:filters.category.label'),
      attribute: `named_tags.${tagPrefix}category`,
      id: 'category',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    collection: new Filter({
      title: t('algolia:filters.collection.label'),
      attribute: `named_tags.${tagPrefix}collection`,
      id: 'collection',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          // Some pages exceed 50 values (Tissot straps has 90+) — anything
          // past the limit was silently cut off, so allow showing them all
          limit: 50,
          showMore: true,
          showMoreLimit: 1000,
          // Sort options alphabetically (default is by result count)
          sortBy: ['name:asc'],
          templates: {
            showMoreText(data, { html }) {
              return html`
              <span>${data.isShowingMore
                ? t('algolia:showLess')
                : t('algolia:showMore')}
              </span>`;
            },
          },
        });
      },
    }),
    gender: new Filter({
      title: t('algolia:filters.gender.label'),
      attribute: `named_tags.${tagPrefix}gender`,
      id: 'gender',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    brand: new Filter({
      title: t('algolia:filters.brand.label'),
      attribute: 'named_tags.brand',
      id: 'brand',
      limit: 50,
      showMore: true,
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
          showMore: true,
          showMoreLimit: 1000,
          templates: {
            showMoreText(data, { html }) {
              return html`
              <span>${data.isShowingMore
                ? t('algolia:showLess')
                : t('algolia:showMore')}
              </span>`;
            },
          },
        });
      },
    }),
    labGrown: new Filter({
      title: t('algolia:filters.labGrown.label'),
      attribute: `named_tags.${tagPrefix}labGrown`,
      id: 'labGrown',
      widgetType: 'toggle',
      generateWidget: function() {
        const filter = this;
        return toggleRefinement({
          container: `#${this.id}`,
          attribute: this.attribute,
          on: 'Yes',
          templates: {
            labelText(_item, { html }) {
              return html`${filter.title}`;
            },
          },
        });
      },
    }),
    gemstone: new Filter({
      title: t('algolia:filters.gemstone.label'),
      attribute: `named_tags.${tagPrefix}gemstone`,
      id: 'gemstone',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    gemstoneShape: new Filter({
      title: t('algolia:filters.gemstoneShape.label'),
      attribute: `named_tags.${tagPrefix}gemstoneShape`,
      id: 'gemstoneShape',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    diamondSettings: new Filter({
      title: t('algolia:filters.diamondSettings.label'),
      attribute: `named_tags.${tagPrefix}diamondSettings`,
      id: 'diamondSettings',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    diamondWeight: new Filter({
      title: t('algolia:filters.diamondWeight.label'),
      attribute: `named_tags.${tagPrefix}overallDiamondWeight`,
      id: 'diamondWeight',
      widgetType: 'range',
      generateWidget: function() {
        return wrapWithPanel(this.title, rangeSlider, {
          container: `#${this.id}`,
          attribute: this.attribute,
          pips: false,
          precision: 2,
          // step: 0.5, // It doesn't look like this supports < 1
          tooltips: {
            // TODO: This needs i18n support?
            format: (rawValue) => `${rawValue} ct`,
          },
          cssClasses: {
            root: 'range-slider',
          },
        });
      },
    }),
    band: new Filter({
      title: t('algolia:filters.band.label'),
      attribute: `named_tags.${tagPrefix}band`,
      id: 'band',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    caseMaterial: new Filter({
      title: t('algolia:filters.caseMaterial.label'),
      attribute: `named_tags.${tagPrefix}caseMaterial`,
      id: 'caseMaterial',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    caseSize: new Filter({
      title: t('algolia:filters.caseSize.label'),
      attribute: `named_tags.${tagPrefix}caseSize`,
      id: 'caseSize',
      widgetType: 'refinementList',
      generateWidget: function() {
        // Fixed order: Large → Medium → Small (anything else falls to the end).
        const order = ['large', 'medium', 'small'];
        const rank = (name: string) => {
          const i = order.indexOf((name || '').trim().toLowerCase());
          return i === -1 ? order.length : i;
        };
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
          sortBy(a, b) {
            return rank(a.name) - rank(b.name);
          },
        });
      },
    }),
    centreStoneShape: new Filter({
      title: t('algolia:filters.centreStoneShape.label'),
      attribute: `named_tags.${tagPrefix}centreStoneShape`,
      id: 'centreStoneShape',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    customization: new Filter({
      title: t('algolia:filters.customization.label'),
      attribute: `named_tags.${tagPrefix}customization`,
      id: 'customization',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    dialColour: new Filter({
      title: t('algolia:filters.dialColour.label'),
      attribute: `named_tags.${tagPrefix}dialColour`,
      id: 'dialColour',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    diameter: new Filter({
      title: t('algolia:filters.diameter.label'),
      attribute: `named_tags.${tagPrefix}diameter`,
      id: 'diameter',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    durability: new Filter({
      title: t('algolia:filters.durability.label'),
      attribute: `named_tags.${tagPrefix}durability`,
      id: 'durability',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    features: new Filter({
      title: t('algolia:filters.features.label'),
      attribute: `named_tags.${tagPrefix}features`,
      id: 'features',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    length: new Filter({
      title: t('algolia:filters.length.label'),
      attribute: `named_tags.${tagPrefix}length`,
      id: 'length',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    material: new Filter({
      title: t('algolia:filters.material.label'),
      attribute: `named_tags.${tagPrefix}material`,
      id: 'material',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    metalColour: new Filter({
      title: t('algolia:filters.metalColour.label'),
      attribute: `named_tags.${tagPrefix}metalColour`,
      id: 'metalColour',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    metalType: new Filter({
      title: t('algolia:filters.metalType.label'),
      attribute: `named_tags.${tagPrefix}metalType`,
      id: 'metalType',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    movement: new Filter({
      title: t('algolia:filters.movement.label'),
      attribute: `named_tags.${tagPrefix}movement`,
      id: 'movement',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    primaryStone: new Filter({
      title: t('algolia:filters.primaryStone.label'),
      attribute: `named_tags.${tagPrefix}primaryStone`,
      id: 'primaryStone',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    style: new Filter({
      title: t('algolia:filters.style.label'),
      attribute: `named_tags.${tagPrefix}style`,
      id: 'style',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    waterResistance: new Filter({
      title: t('algolia:filters.waterResistance.label'),
      attribute: `named_tags.${tagPrefix}waterResistance`,
      id: 'waterResistance',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
          sortBy(a, b) {
            // For these fields, we want to sort by number, not alphabetically
            const aNum = parseFirstNumber(a.name);
            const bNum = parseFirstNumber(b.name);
            if (aNum === null) {
              return 1;
            }
            if (bNum === null) {
              return -1;
            }
            return aNum - bNum;
          },
        });
      },
    }),
    powerReserve: new Filter({
      title: t('algolia:filters.powerReserve.label'),
      attribute: `named_tags.${tagPrefix}powerReserve`,
      id: 'powerReserve',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
          sortBy(a, b) {
            // For these fields, we want to sort by number, not alphabetically
            const aNum = parseFirstNumber(a.name);
            const bNum = parseFirstNumber(b.name);
            if (aNum === null) {
              return 1;
            }
            if (bNum === null) {
              return -1;
            }
            return aNum - bNum;
          },
        });
      },
    }),
    earringStyle: new Filter({
      title: t('algolia:filters.earringStyle.label'),
      attribute: `named_tags.${tagPrefix}earringStyle`,
      id: 'earringStyle',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    braceletStyle: new Filter({
      title: t('algolia:filters.braceletStyle.label'),
      attribute: `named_tags.${tagPrefix}braceletStyle`,
      id: 'braceletStyle',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    bandType: new Filter({
      title: t('algolia:filters.bandType.label'),
      attribute: `named_tags.${tagPrefix}bandType`,
      id: 'bandType',
      widgetType: 'refinementList',
      generateWidget: function() {
        return wrapWithPanel(this.title, refinementList, {
          container: `#${this.id}`,
          attribute: this.attribute,
          limit: 50,
        });
      },
    }),
    giaCertified: new Filter({
      title: t('algolia:filters.giaCertified.label'),
      attribute: `named_tags.${tagPrefix}GIAcertified`,
      id: 'giaCertified',
      widgetType: 'toggle',
      generateWidget: function() {
        const filter = this;
        return toggleRefinement({
          container: `#${this.id}`,
          attribute: this.attribute,
          on: 'Yes',
          templates: {
            labelText(_item, { html }) {
              return html`${filter.title}`;
            },
          },
        });
      },
    }),
    limitedEdition: new Filter({
      title: t('algolia:filters.limitedEdition.label'),
      attribute: `named_tags.${tagPrefix}limitedEdition`,
      id: 'limitedEdition',
      widgetType: 'toggle',
      generateWidget: function() {
        const filter = this;
        return toggleRefinement({
          container: `#${this.id}`,
          attribute: this.attribute,
          on: 'Yes',
          templates: {
            labelText(_item, { html }) {
              return html`${filter.title}`;
            },
          },
        });
      },
    }),
    newArrival: new Filter({
      title: t('algolia:filters.newArrival.label'),
      attribute: `named_tags.${tagPrefix}newArrival`,
      id: 'newArrival',
      widgetType: 'toggle',
      generateWidget: function() {
        const filter = this;
        return toggleRefinement({
          container: `#${this.id}`,
          attribute: this.attribute,
          on: 'Yes',
          templates: {
            labelText(_item, { html }) {
              return html`${filter.title}`;
            },
          },
        });
      },
    }),
  };

  return filterObjects;
};

/** Return all filters except for specific ones */
function allFilterObjectsExcept(filterIds: string[], locale: string, t: TFunction) {
  return Object.values(filterObjects(locale, t)).filter((filter) => !filterIds.includes(filter.id));
}

/** Return only the given filters, in the order listed. */
function orderedFilterObjects(filterIds: string[], locale: string, t: TFunction) {
  const all = filterObjects(locale, t);
  return filterIds.map((id) => all[id]).filter(Boolean);
}

/** Ordered jewellery filter set, with "Product Type" renamed "Jewellery Type" */
function jewelleryFilterSet(filterIds: string[], locale: string, t: TFunction) {
  const filters = orderedFilterObjects(filterIds, locale, t);
  const category = filters.find((filter) => filter.id === 'category');
  if (category) {
    category.title = t('algolia:filters.category.jewelleryLabel');
  }
  return filters;
}

/** Single-brand fine-jewellery pages (plus the pearl collection pages, which
 * share the arrangement): Price · Jewellery Type · Style · Material ·
 * Gemstone · Collection · Available Online · Available Location.
 * No Brand filter; Jewellery Type expands by default. */
const JEWELLERY_BRAND_PAGE_HANDLES = [
  'fine-jewellery-raffi-co-collection',
  'fine-jewellery-raffi-co-1',
  'fine-jewellery-mikimoto',
  'fine-jewellery-roberto-coin-1',
  'gucci-fine-jewellery',
  'fine-jewellery-hearts-on-fire-1',
  'fine-jewellery-simon-g-1',
  'fine-jewellery-hulchi-belluni',
  'leo-pizzo',
  'uneek',
  'pearls',
  'pearl-jewellery',
];

/** Fine-jewellery category pages: Price · Brand · Style · Material ·
 * Gemstone · Collection · Available Online · Available Location.
 * No Jewellery Type filter; Style expands by default alongside Brand. */
const JEWELLERY_CATEGORY_PAGE_HANDLES = [
  'necklaces-pendants',
  'earrings',
  'bracelets',
  'fine-jewellery-rings',
];

/** Filter-set identifiers that get the jewellery accordion behaviour
 * (static Available Location + Browse By Category items moved to the end) */
export const JEWELLERY_FILTER_SETS = [
  'jewellery',
  'collection-fine-jewellery-all-brands',
  ...JEWELLERY_BRAND_PAGE_HANDLES,
  ...JEWELLERY_CATEGORY_PAGE_HANDLES,
];

/** Sets where the Jewellery Type accordion starts expanded */
export const JEWELLERY_TYPE_OPEN_SETS = [
  'jewellery',
  'collection-fine-jewellery-all-brands',
  ...JEWELLERY_BRAND_PAGE_HANDLES,
];

/** Sets where the Style accordion starts expanded */
export const STYLE_OPEN_SETS = [...JEWELLERY_CATEGORY_PAGE_HANDLES];

/** Map of the filter objects to use for each collection, by handle */
const collectionFilterObjects = (locale: string, t: TFunction) => {
  const collectionFilterObjects: Record<string, Filter[]> = {
    // Just show all by default
    default: [...Object.values(filterObjects(locale, t))],

    // Special collections (and their subcategories)
    // Timepieces: an explicit, ordered list. "Shop By Price" (price widget) and
    // "Available Location" (static numeric menu) are rendered separately by the template;
    // instant-search.ts moves Available Location to the end so the final order matches:
    // Price · Gender · Brand · Collection · Case Size · Material · Movement ·
    // Water Resistance · Available Online · Available Location.
    // Single-option filters (e.g. Brand on a one-brand page) auto-hide via the panel.
    'timepieces': orderedFilterObjects(
      ['gender', 'brand', 'collection', 'caseSize', 'material', 'movement', 'waterResistance', 'availableOnline'],
      locale,
      t,
    ),
    // Jewellery: an explicit, ordered list (like timepieces). Final order:
    // Price (template) · Brand · Jewellery Type · Style · Material · Gemstone ·
    // Collection · Available Online · Available Location (moved to the end by
    // instant-search.ts). "Product Type" is renamed "Jewellery Type" here.
    'jewellery': jewelleryFilterSet(
      ['brand', 'category', 'style', 'material', 'gemstone', 'collection', 'availableOnline'],
      locale,
      t,
    ),
    // The fine-jewellery landing page alone puts Jewellery Type above Brand
    'collection-fine-jewellery-all-brands': jewelleryFilterSet(
      ['category', 'brand', 'style', 'material', 'gemstone', 'collection', 'availableOnline'],
      locale,
      t,
    ),
    // Single-brand + pearl fine-jewellery pages: no Brand filter
    ...Object.fromEntries(JEWELLERY_BRAND_PAGE_HANDLES.map((handle) => [
      handle,
      jewelleryFilterSet(
        ['category', 'style', 'material', 'gemstone', 'collection', 'availableOnline'],
        locale,
        t,
      ),
    ])),
    // Fine-jewellery category pages: no Jewellery Type filter
    ...Object.fromEntries(JEWELLERY_CATEGORY_PAGE_HANDLES.map((handle) => [
      handle,
      jewelleryFilterSet(
        ['brand', 'style', 'material', 'gemstone', 'collection', 'availableOnline'],
        locale,
        t,
      ),
    ])),
    'wedding-engagement': allFilterObjectsExcept(['limitedEdition', 'availableOnline'], locale, t),
    'gifts-accessories': allFilterObjectsExcept(['labGrown', 'diamondWeight', 'giaCertified'], locale, t),
  };

  return collectionFilterObjects;
};

/**
 * Get the identifier to use for grabbing the filter options
 * @param collectionHandle Handle of the collection we're getting filters for
 * @param locale The locale to use for the filter options
 * @param t The i18n function to use for translations
 * @param slug Technically not the slug, technically the "name" of the collection page in contentful, I believe,
 *   But it's got the parent collection in it, which is what I need to make the children match the parent
 * @returns The identifier to use for the filter options
 */
export function getFilterSetIdentifier(
  collectionHandle: string,
  locale: string,
  t: TFunction,
  slug?: string,
) {
  // If there is a specific set for the collection handle, use the handle
  if (collectionFilterObjects(locale, t)[collectionHandle]) {
    return collectionHandle;
  } else if (collectionHandle === 'bestsellers') {
    // Virtual popularity-driven collection — watch-dominated, and the
    // 'default' set (every filter) is too noisy
    return 'timepieces';
  } else if (
    // Check collection categories, essentially
    collectionHandle === 'new-old-stock'
    // Match the collection handle too (e.g. "timepieces-tudor"), not just the page slug
    || collectionHandle.indexOf('timepieces') !== -1
    || slug?.indexOf('timepieces') !== -1
  ) {
    return 'timepieces';
  } else if (slug?.indexOf('jewellery') !== -1) {
    return 'jewellery';
  } else if (
    (slug?.indexOf('wedding') !== -1)
    || (slug?.indexOf('engagement') !== -1)
  ) {
    return 'wedding-engagement';
  } else if (slug?.indexOf('gifts-accessories') !== -1) {
    return 'gifts-accessories';
  }

  return 'default';
}

/** Get the filters to use for a given collection handle */
export default function getFilterObjects(
  collectionHandle: string,
  locale: string,
  t: TFunction,
  slug?: string,
): Filter[] {
  const filterIdentifier = getFilterSetIdentifier(collectionHandle, locale, t, slug);
  // console.debug('Filter objects identifier:', filterIdentifier);
  const filters = collectionFilterObjects(locale, t)[filterIdentifier];
  if (!filters) {
    return [];
  }
  return filters;
}
